/**
 * Session Debug Widget - Development Only
 *
 * Shows real-time session timing information in the upper right corner:
 * - Inactivity timer countdown
 * - JWT token expiry countdown
 * - Token refresh notifications
 *
 * Only renders in development mode (localhost)
 */

import { useState, useEffect, useRef } from 'react';
import { getTokenInfo, isAuthenticated } from '../utils/tokenUtils';

import { safeStorage } from '../utils/safeStorage';
// Only show in development. Use Vite's canonical import.meta.env.DEV — it is
// statically replaced at build time, so this whole component tree-shakes out
// of production bundles (process.env.NODE_ENV only worked via a Vite shim).
const isDev = import.meta.env.DEV;

/** Clear both the desktop action bar and its two-row narrow-screen layout. */
const WIDGET_RIGHT = '24px';

// Storage key for activity timestamp (must match InactivityMonitor)
const ACTIVITY_STORAGE_KEY = 'evv_last_activity_timestamp';
// Development: 60 minutes, Production: 30 minutes (must match InactivityMonitor)
const INACTIVITY_TIMEOUT = isDev
  ? 60 * 60 * 1000   // 60 minutes in dev
  : 30 * 60 * 1000;  // 30 minutes in production

const SessionDebugWidget = () => {
  const [inactivityRemaining, setInactivityRemaining] = useState(null);
  const [tokenRemaining, setTokenRemaining] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  // Default to collapsed so the widget reads as a small corner pill instead of
  // a panel overlapping the header chrome. Devs can expand it on demand.
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [flashRefresh, setFlashRefresh] = useState(false);
  const [lastActivityTime, setLastActivityTime] = useState(null);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(isAuthenticated());
  const previousTokenExpiry = useRef(null);

  // Listen for auth state changes (login/logout)
  useEffect(() => {
    if (!isDev) return;

    const checkAuth = () => {
      setIsUserAuthenticated(isAuthenticated());
    };

    // Check on storage changes (login/logout in this or other tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'evv_token' || e.key === null) {
        checkAuth();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Also check periodically for same-tab login
    const interval = setInterval(checkAuth, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Track activity and update localStorage timestamp
  useEffect(() => {
    if (!isDev || !isUserAuthenticated) return;

    const handleActivity = () => {
      const now = Date.now();
      safeStorage.setItem(ACTIVITY_STORAGE_KEY, now.toString());
      // Don't log - too noisy
    };

    // Debounce activity updates
    let debounceTimer = null;
    const debouncedActivity = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(handleActivity, 300);
    };

    // Listen for activity events
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, debouncedActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, debouncedActivity);
      });
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [isUserAuthenticated]);

  useEffect(() => {
    if (!isDev || !isUserAuthenticated) return;

    const updateTimers = () => {
      // Calculate inactivity remaining from localStorage timestamp
      const storedTimestamp = safeStorage.getItem(ACTIVITY_STORAGE_KEY);
      const now = Date.now();
      let inactivityLeft = INACTIVITY_TIMEOUT; // Default to full time

      // Check localStorage for activity timestamp

      if (storedTimestamp && storedTimestamp.length > 0) {
        const lastActivity = parseInt(storedTimestamp, 10);
        if (!isNaN(lastActivity) && lastActivity > 0) {
          const elapsed = now - lastActivity;
          inactivityLeft = Math.max(0, INACTIVITY_TIMEOUT - elapsed);
          setLastActivityTime(new Date(lastActivity));
        } else {
          // Invalid value - reinitialize
          console.log('🕐 Invalid timestamp, reinitializing:', storedTimestamp);
          safeStorage.setItem(ACTIVITY_STORAGE_KEY, now.toString());
          setLastActivityTime(new Date(now));
          inactivityLeft = INACTIVITY_TIMEOUT;
        }
      } else {
        // No timestamp exists - initialize it
        console.log('🕐 No timestamp found, initializing');
        safeStorage.setItem(ACTIVITY_STORAGE_KEY, now.toString());
        setLastActivityTime(new Date(now));
        inactivityLeft = INACTIVITY_TIMEOUT;
      }
      setInactivityRemaining(inactivityLeft);

      // Calculate token remaining
      const tokenInfo = getTokenInfo();
      if (tokenInfo.exists && !tokenInfo.expired) {
        setTokenRemaining(tokenInfo.timeUntilExpiry);

        // Detect token refresh (expiry time jumped up)
        if (previousTokenExpiry.current !== null) {
          const previousExpiry = previousTokenExpiry.current;
          const currentExpiry = tokenInfo.timeUntilExpiry;

          // If token expiry increased by more than 1 minute, it was refreshed
          if (currentExpiry > previousExpiry + 60) {
            setLastRefresh(new Date());
            setFlashRefresh(true);
            setTimeout(() => setFlashRefresh(false), 3000);
          }
        }
        previousTokenExpiry.current = tokenInfo.timeUntilExpiry;
      } else {
        setTokenRemaining(null);
      }
    };

    // Update immediately
    updateTimers();

    // Update every second
    const interval = setInterval(updateTimers, 1000);

    return () => clearInterval(interval);
  }, [isUserAuthenticated]);

  // Don't render in production or if not authenticated
  if (!isDev || !isUserAuthenticated) {
    return null;
  }

  // Format time as MM:SS or HH:MM:SS
  const formatTime = (ms) => {
    if (ms === null || ms === undefined) return '--:--';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format time for token (show hours:minutes:seconds)
  const formatTokenTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '--:--:--';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    // Always show full format for clarity
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Determine inactivity color
  const getInactivityColor = () => {
    if (inactivityRemaining === null) return '#6b7280';
    const minutes = inactivityRemaining / 60000;
    if (minutes <= 1) return '#dc2626'; // Red - critical
    if (minutes <= 5) return '#f59e0b'; // Orange - warning
    return '#10b981'; // Green - safe
  };

  // Determine token color
  const getTokenColor = () => {
    if (tokenRemaining === null) return '#6b7280';
    const minutes = tokenRemaining / 60;
    if (minutes <= 5) return '#dc2626'; // Red - critical
    if (minutes <= 30) return '#f59e0b'; // Orange - warning
    return '#10b981'; // Green - safe
  };

  if (isCollapsed) {
    return (
      <div
        className="arasync-session-debug bottom-[160px] sm:bottom-[88px]"
        onClick={() => setIsCollapsed(false)}
        style={{
          position: 'fixed',
          // Bottom right, clear of the header (it used to sit over the theme
          // toggle), above the chat button and above a document window's
          // bottom bar (at 14px it covered the Complete button).
          right: WIDGET_RIGHT,
          backgroundColor: flashRefresh ? '#10b981' : '#1f2937',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '10px',
          fontFamily: 'monospace',
          cursor: 'pointer',
          zIndex: 99998,
          opacity: 0.9,
          transition: 'background-color 0.3s',
        }}
        title="Click to expand session debug"
      >
        🔐 {formatTime(inactivityRemaining)}
      </div>
    );
  }

  return (
    <div
      className="arasync-session-debug bottom-[160px] sm:bottom-[88px]"
      style={{
        position: 'fixed',
        right: WIDGET_RIGHT,
        backgroundColor: flashRefresh ? '#065f46' : '#1f2937',
        color: 'white',
        padding: '6px 10px',
        borderRadius: '6px',
        fontSize: '10px',
        fontFamily: 'monospace',
        zIndex: 99998,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
        minWidth: '150px',
        transition: 'background-color 0.3s',
      }}
    >
      {/* Header row with collapse button */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '4px',
      }}>
        <span style={{ color: '#9ca3af', fontSize: '8px' }}>🔐 DEV</span>
        <button
          onClick={() => setIsCollapsed(true)}
          style={{
            background: 'none',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            fontSize: '12px',
            padding: '0',
            lineHeight: 1,
          }}
          title="Collapse"
        >
          ✕
        </button>
      </div>

      {/* Compact timers row */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        {/* Inactivity Timer */}
        <div>
          <div style={{ color: '#9ca3af', fontSize: '7px', marginBottom: '1px' }}>
            IDLE
          </div>
          <div style={{
            color: getInactivityColor(),
            fontSize: '13px',
            fontWeight: 'bold',
          }}>
            {formatTime(inactivityRemaining)}
          </div>
        </div>

        {/* Divider */}
        <div style={{ width: '1px', height: '24px', backgroundColor: '#374151' }} />

        {/* Token Expiry */}
        <div>
          <div style={{ color: '#9ca3af', fontSize: '7px', marginBottom: '1px' }}>
            JWT
          </div>
          <div style={{
            color: getTokenColor(),
            fontSize: '13px',
            fontWeight: 'bold',
          }}>
            {formatTokenTime(tokenRemaining)}
          </div>
        </div>
      </div>

      {/* Last Activity Time - for debugging */}
      <div style={{
        marginTop: '4px',
        paddingTop: '3px',
        borderTop: '1px solid #374151',
        color: '#9ca3af',
        fontSize: '7px',
      }}>
        Last activity: {lastActivityTime ? lastActivityTime.toLocaleTimeString([], { hour12: true }) : 'N/A'}
      </div>

      {/* Last Refresh - compact */}
      {lastRefresh && (
        <div style={{
          color: '#10b981',
          fontSize: '7px',
        }}>
          ✓ Token refreshed {lastRefresh.toLocaleTimeString([], { hour12: true })}
        </div>
      )}

      {/* Flash notification */}
      {flashRefresh && (
        <div style={{
          position: 'absolute',
          top: '-6px',
          right: '-6px',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '1px 4px',
          borderRadius: '8px',
          fontSize: '7px',
          fontWeight: 'bold',
        }}>
          REFRESHED!
        </div>
      )}
    </div>
  );
};

export default SessionDebugWidget;
